/** Código HTTP de recusa por regra de negócio. */
const BAD_REQUEST = 400;
/** Código HTTP de recusa por permissão. */
const FORBIDDEN = 403;
/** A partir daqui a falha é do servidor, não do que o operador enviou. */
const SERVER_ERROR = 500;

/**
 * Natureza da falha ao gravar um plano financeiro.
 *
 * A distinção existe porque cada uma pede uma resposta diferente da tela:
 * `business` traz uma mensagem que o operador precisa ler e agir; `forbidden`
 * significa que não adianta tentar de novo; `server` é falha do backend, onde a
 * tela só pode avisar e sugerir nova tentativa.
 */
export type PlanWriteErrorKind = 'business' | 'forbidden' | 'server' | 'unknown';

/** Falha classificada de um `POST`/`PUT` de plano. */
export interface PlanWriteError {
  readonly kind: PlanWriteErrorKind;
  /** Mensagem do backend, quando ele mandou uma legível. */
  readonly message: string | null;
  /** Status HTTP, quando o erro expõe um. */
  readonly status: number | null;
}

/**
 * Lê uma propriedade de um valor desconhecido sem recorrer a `any`.
 *
 * @param source - Valor de origem.
 * @param key - Nome da propriedade.
 * @returns O valor da propriedade, ou `undefined` quando a origem não é objeto.
 */
function readProperty(source: unknown, key: string): unknown {
  if (typeof source !== 'object' || source === null) return undefined;
  return (source as Record<string, unknown>)[key];
}

/**
 * Extrai o status HTTP de um erro do Axios sem recorrer a `any`.
 *
 * @param error - Erro devolvido pela mutation.
 * @returns Status HTTP, ou `null` quando o erro não expõe um.
 */
export function readStatus(error: unknown): number | null {
  const status = readProperty(readProperty(error, 'response'), 'status');
  return typeof status === 'number' ? status : null;
}

/** Texto útil, ou `null` quando o valor é vazio ou não é string. */
function readText(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value.trim() : null;
}

/**
 * Junta uma coleção de erros de validação numa frase só.
 *
 * Cobre as três formas que o backend usa: lista de strings, lista de objetos com
 * a mensagem dentro, e o `ModelState` do ASP.NET Core — um objeto de campo para
 * lista de mensagens.
 *
 * @param value - Valor do campo `errors`/`Errors` do corpo.
 * @returns As mensagens concatenadas, ou `null` quando não há nenhuma.
 */
function joinErrors(value: unknown): string | null {
  const parts: string[] = [];

  if (Array.isArray(value)) {
    for (const item of value) {
      const text =
        readText(item) ??
        readText(readProperty(item, 'message')) ??
        readText(readProperty(item, 'Message'));
      if (text !== null) parts.push(text);
    }
  } else if (typeof value === 'object' && value !== null) {
    for (const entry of Object.values(value as Record<string, unknown>)) {
      for (const item of Array.isArray(entry) ? entry : [entry]) {
        const text = readText(item);
        if (text !== null) parts.push(text);
      }
    }
  }

  return parts.length === 0 ? null : parts.join(' ');
}

/**
 * Extrai a mensagem devolvida pelo backend.
 *
 * O corpo de erro não tem forma declarada no spec — os tipos gerados são `any` —,
 * então as formas conhecidas são testadas em ordem: corpo que já é a string, os
 * campos de mensagem e, por último, a coleção de erros de validação.
 *
 * **As duas grafias são testadas.** Os endpoints de pagamento individual
 * respondem em PascalCase (`{ "Message": "Esta ação não é permitida.", "Errors":
 * [] }`), enquanto o padrão do ASP.NET Core é camelCase. Procurar só `message`
 * fazia a mensagem ser descartada e a tela cair no texto genérico — justamente
 * nos casos em que o backend explicou o motivo.
 *
 * @param error - Erro devolvido pela mutation.
 * @returns Mensagem do backend, ou `null`.
 */
export function readBusinessMessage(error: unknown): string | null {
  const data = readProperty(readProperty(error, 'response'), 'data');

  const body = readText(data);
  if (body !== null) return body;

  for (const key of ['message', 'Message', 'title', 'Title', 'detail', 'Detail']) {
    const value = readText(readProperty(data, key));
    if (value !== null) return value;
  }

  // `Errors` costuma vir vazio ao lado de `Message`; quando traz itens, são as
  // recusas de validação campo a campo, e todas importam para o operador.
  for (const key of ['errors', 'Errors']) {
    const joined = joinErrors(readProperty(data, key));
    if (joined !== null) return joined;
  }

  return null;
}

/**
 * Classifica a falha de gravação de um plano financeiro.
 *
 * Usada pela criação e pela edição: as duas escrevem o mesmo
 * `IndividualPaymentSaveViewModel` e falham pelos mesmos motivos, então a leitura
 * do erro é uma só.
 *
 * O `5xx` importa em particular porque **não há tratamento global**: o
 * interceptor do Axios apenas repassa o erro adiante — o toast que o comentário
 * dele menciona nunca chegou a existir no `AppProviders`. Sem esta classificação,
 * um `500` no salvamento não produzia nenhum sinal na tela.
 *
 * @param error - Erro devolvido pela mutation.
 * @returns Natureza da falha, mensagem do backend e status.
 *
 * @example
 * ```ts
 * classifyPlanWriteError(error); // { kind: 'server', message: null, status: 500 }
 * ```
 */
export function classifyPlanWriteError(error: unknown): PlanWriteError {
  const status = readStatus(error);
  const message = readBusinessMessage(error);

  if (status === BAD_REQUEST) return { kind: 'business', message, status };
  if (status === FORBIDDEN) return { kind: 'forbidden', message, status };
  if (status !== null && status >= SERVER_ERROR) return { kind: 'server', message, status };

  return { kind: 'unknown', message, status };
}
